import React from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { SystemAlertContainer } from '../notifications';
import { SkipLinks } from '../common';
import { useResponsiveNavigation } from '../../hooks/useResponsive';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useI18n } from '../../contexts/I18nContext';

export const MainLayout: React.FC = () => {
  const { isNavigationOpen, openNavigation, closeNavigation } = useResponsiveNavigation();
  const { preferences } = usePreferences();
  const { isRTL } = useI18n();

  // Apply layout preferences
  const layoutClasses = {
    compact: 'py-4',
    comfortable: 'py-6',
    spacious: 'py-8',
  };

  const containerClasses = {
    compact: 'max-w-6xl mx-auto px-3 sm:px-4 lg:px-6',
    comfortable: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
    spacious: 'max-w-full mx-auto px-6 sm:px-8 lg:px-12',
  };

  const rootClasses = [
    'min-h-screen',
    preferences.theme === 'dark' ? 'dark' : '',
    preferences.dashboardLayout === 'spacious' ? 'large-text' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`${rootClasses} flex min-h-screen`} dir={isRTL ? 'rtl' : 'ltr'} style={{ backgroundColor: 'var(--color-background)' }}>
      {/* Skip links for accessibility */}
      <SkipLinks />
      
      <ErrorBoundary>
        {/* Sidebar - positioned based on RTL */}
        <Sidebar isOpen={isNavigationOpen} onClose={closeNavigation} />

        {/* Main content area - proper responsive layout */}
        <div className={`layout-content flex-1 flex flex-col min-w-0 ${isRTL ? 'lg:mr-64 lg:ml-0' : 'lg:ml-64 lg:mr-0'}`}>
          {/* Header - full width of remaining space */}
          <Header onMenuClick={openNavigation} />

          {/* Page content */}
          <main 
            id="main-content" 
            className="flex-1 min-h-0"
            role="main"
            aria-label="Main content"
          >
            <div className={layoutClasses[preferences.dashboardLayout]}>
              <div className={containerClasses[preferences.dashboardLayout]}>
                {/* System alerts */}
                <SystemAlertContainer />
                
                <ErrorBoundary>
                  <Outlet />
                </ErrorBoundary>
              </div>
            </div>
          </main>
        </div>
      </ErrorBoundary>
    </div>
  );
};